import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AiImageBadge } from "@/components/AiImageBadge";
import {
  fetchTeaserEventsForCity,
  fetchTeaserVenuesForCity,
  type TeaserEvent,
  type TeaserVenue,
} from "@/lib/teaserDiscovery";

const CITY = "Zurich";

function TeaserCard({
  photoUrl,
  category,
  kind,
}: {
  photoUrl: string | null;
  category: string;
  kind: "venue" | "event";
}) {
  return (
    <Link
      to="/user/login"
      className="relative w-[148px] shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-[#141416]"
    >
      <div className="relative h-20 w-full overflow-hidden bg-[#1a1a1c]">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="h-full w-full object-cover" />
        ) : null}
        {photoUrl ? <AiImageBadge size="sm" /> : null}
        <span className="absolute bottom-1.5 left-1.5 rounded-full bg-black/50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/90">
          {category}
        </span>
      </div>
      <div className="relative px-2.5 py-2.5">
        <div className="pointer-events-none absolute inset-0 backdrop-blur-[6px] bg-[#0B0B0C]/55" />
        <p className="relative text-center text-[10px] text-white/55">
          {kind === "event" ? "Heute" : category}
        </p>
      </div>
    </Link>
  );
}

/**
 * Live-mode logged-out home — teaser rails + sticky auth CTA (parity with mobile Phase 1).
 */
export function DiscoveryTeaserHome() {
  const { t } = useTranslation("consumer");
  const [venues, setVenues] = useState<TeaserVenue[]>([]);
  const [events, setEvents] = useState<TeaserEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [v, e] = await Promise.all([
          fetchTeaserVenuesForCity(CITY),
          fetchTeaserEventsForCity(CITY),
        ]);
        if (!cancelled) {
          setVenues(v);
          setEvents(e);
        }
      } catch {
        if (!cancelled) {
          setVenues([]);
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative min-h-dvh bg-[#0B0B0C] text-[#F4F1EA]" data-font-group="premium">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_at_top,_rgba(212,168,75,0.12),_transparent_55%)]" />

      <header className="relative px-5 pb-3 pt-14">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
          Heute Abend
        </p>
        <h1
          className="mt-1 text-3xl leading-tight text-[#F4F1EA]"
          style={{ fontFamily: "var(--font-caslon)" }}
        >
          Zürich
        </h1>
      </header>

      <main className="relative px-5 pb-28">
        {loading ? (
          <div className="mt-4 flex gap-3 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-44 w-[148px] shrink-0 animate-pulse rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : (
          <>
            <section>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-white/50">
                  Highlights
                </p>
                <p className="text-xs text-white/35">{venues.length}</p>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {venues.map((venue) => (
                  <TeaserCard
                    key={venue.id}
                    photoUrl={venue.photo_url}
                    category={venue.category}
                    kind="venue"
                  />
                ))}
              </div>
            </section>

            <section className="mt-8">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-white/50">
                  Live & Current Events
                </p>
                <p className="text-xs text-white/35">{events.length}</p>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {events.map((event) => (
                  <TeaserCard
                    key={event.id}
                    photoUrl={event.photo_url}
                    category={event.venueCategory ?? "Event"}
                    kind="event"
                  />
                ))}
              </div>
            </section>

            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm font-semibold text-white">
                {t("onboarding.discoverTitle")}
              </p>
              <p className="mt-1 text-xs text-white/50">{t("onboarding.discoverBody")}</p>
              <div className="mt-3 flex gap-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-24 flex-1 rounded-xl bg-white/5" />
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-white/10 bg-[#0B0B0C]/95 px-5 pb-6 pt-3 backdrop-blur-md">
        <Link
          to="/user/login"
          className="flex w-full items-center justify-center rounded-full bg-[#D4A84B] px-5 py-3.5 text-sm font-bold text-[#0B0B0C]"
        >
          {t("discovery.teaser.cta")}
        </Link>
      </div>
    </div>
  );
}
